import type { DocumentV1 } from '@alpod-ai-writer/backend';
import { Button, Label, Modal, type ModalProps, Select, Textarea, TextInput } from 'flowbite-react';
import { AlertTriangleIcon, CheckIcon, Languages, RefreshCwIcon, SparklesIcon, StopCircleIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

const MAX_AUTO_RETRIES = 5;

import { useAITask } from '../../hooks/useAiTask.hook';
import { Prompt_SystemCitation, Prompt_SystemCitationTranslate, Prompt_UserCitation, Prompt_UserCitationTranslate } from '../../prompts';
import { AITaskSteps } from './aiTaskSteps.component';

type SourceType = 'auto' | 'book_chapter';

interface FormData {
    sourceType: SourceType;
    sourceDescription: string;
    // book chapter fields
    bookSource: string;
    chapterTitle: string;
    chapterAuthors: string;
    chapterPages: string;
}

export interface AiCitationModalProps extends ModalProps {
    document: DocumentV1.Record;
    sourceDescription: string;
    onApply: (citation: string, intext?: string) => void;
}

const parseAiOutput = (text: string): { citation: string; intext: string } => {
    const citationMatch = text.match(/<citation>\s*([\s\S]*?)\s*<\/citation>/);
    const intextMatch = text.match(/<intext>\s*([\s\S]*?)\s*<\/intext>/);
    const intext = intextMatch ? intextMatch[1].trim() : '';
    return {
        citation: citationMatch ? citationMatch[1].trim() : text.trim(),
        intext: intext.replace(/^\(+|\)+$/g, '').replace(/^\[+|\]+$/g, '').trim(),
    };
};

function buildSourcePrompt(values: FormData): string {
    if (values.sourceType === 'book_chapter') {
        const lines = ['Typ zdroje: Kapitola z knihy'];
        if (values.bookSource.trim()) lines.push(`Kniha (ISBN / URL / název): ${values.bookSource.trim()}`);
        if (values.chapterTitle.trim()) lines.push(`Název kapitoly: ${values.chapterTitle.trim()}`);
        if (values.chapterAuthors.trim()) lines.push(`Autor(i) kapitoly: ${values.chapterAuthors.trim()}`);
        if (values.chapterPages.trim()) lines.push(`Strany kapitoly: ${values.chapterPages.trim()}`);
        return lines.join('\n');
    }
    return values.sourceDescription;
}

export const AiCitationModal = ({
    show,
    document,
    sourceDescription,
    onApply,
    onClose
}: AiCitationModalProps) => {
    const aiTask = useAITask({
        documentId: document.documentId
    });

    const { status, lastAssistantMessage } = aiTask;

    const { register, watch, formState: { errors }, getValues } = useForm<FormData>({
        defaultValues: {
            sourceType: 'auto',
            sourceDescription: sourceDescription,
            bookSource: '',
            chapterTitle: '',
            chapterAuthors: '',
            chapterPages: '',
        },
    });

    const sourceType = watch('sourceType');

    const [ hasStarted, setHasStarted ] = useState(false);
    const autoRetryCount = useRef(0);
    const manualAttemptNumber = useRef(0);

    const submitGenerate = useCallback((isAutoRetry: boolean) => {
        if (!isAutoRetry) {
            autoRetryCount.current = 0;
        }
        manualAttemptNumber.current = (manualAttemptNumber.current % MAX_AUTO_RETRIES) + 1;
        const values = getValues();

        const systemPrompt = Prompt_SystemCitation({
            document: document
        });

        const builtSource = buildSourcePrompt(values);
        const userPrompt = Prompt_UserCitation(document.template!.config, builtSource, manualAttemptNumber.current);

        setHasStarted(true);
        aiTask.submit(systemPrompt, userPrompt);
    }, [ aiTask, document, getValues ]);

    const handleGenerate = useCallback(() => submitGenerate(false), [ submitGenerate ]);

    const parsedOutput = useMemo(() => {
        if (!lastAssistantMessage) return null;
        const raw = lastAssistantMessage.parts
            .filter(part => part.type === 'text')
            .map(part => part.type === 'text' ? part.text : '')
            .join('');
        const parsed = parseAiOutput(raw);
        return { raw, ...parsed };
    }, [ lastAssistantMessage ]);

    const lastMessageText = parsedOutput?.citation ?? '';

    const submitGenerateRef = useRef(submitGenerate);
    useEffect(() => { submitGenerateRef.current = submitGenerate; }, [ submitGenerate ]);

    useEffect(() => {
        if (status !== 'ready' || !parsedOutput) return;
        if (parsedOutput.raw.startsWith('Chyba:') && autoRetryCount.current < MAX_AUTO_RETRIES) {
            autoRetryCount.current += 1;
            submitGenerateRef.current(true);
        }
    }, [ status, parsedOutput ]);

    const hasNonLatinScript = useMemo(
        () => /[Ѐ-ӿ؀-ۿ一-鿿぀-ゟ゠-ヿ]/u.test(lastMessageText),
        [ lastMessageText ]
    );

    const handleStop = useCallback(() => {
        aiTask.stop(true);
    }, [ aiTask ]);

    const handleApply = useCallback(() => {
        if (parsedOutput?.citation) {
            onApply(parsedOutput.citation, parsedOutput.intext || undefined);
        }
        onClose?.();
    }, [ parsedOutput, onApply, onClose ]);

    const handleTranslate = useCallback(() => {
        if (!lastMessageText) return;
        aiTask.submit(Prompt_SystemCitationTranslate({ document }), Prompt_UserCitationTranslate(lastMessageText));
    }, [ aiTask, document, lastMessageText ]);

    return (
        <Modal show={show} size="xl" onClose={onClose} popup>
            <Modal.Header>
                <span className="flex flex-row items-start md:items-center leading-5 justify-center gap-2 p-3 text-base md:text-lg font-semibold text-gray-800 dark:text-gray-400">
                    <SparklesIcon size={20} className='flex-shrink-0' />
                    <span>Vygenerovat citaci pomocí AI</span>
                </span>
            </Modal.Header>
            <Modal.Body>
                <div className='w-full space-y-3'>
                    <div>
                        <Label htmlFor="sourceType" className="block mb-1">Typ zdroje:</Label>
                        <Select id="sourceType" {...register('sourceType')}>
                            <option value="auto">Automaticky rozpoznat</option>
                            <option value="book_chapter">Kapitola z knihy</option>
                        </Select>
                    </div>

                    {sourceType === 'auto' && (
                        <div>
                            <Label htmlFor="source" className="block mb-1">Informace o zdroji:</Label>
                            <div className='flex flex-row items-start gap-2'>
                                <Textarea
                                    id="source"
                                    placeholder="https://odborny.web.cz/"
                                    {...register('sourceDescription', {
                                        maxLength: { value: 1000, message: 'Max. délka je 1000 znaků.' },
                                        required: { value: true, message: 'Toto pole je povinné.' },
                                    })}
                                    rows={2}
                                    className='grow'
                                    color={errors.sourceDescription ? 'failure' : 'default'}
                                    helperText={errors.sourceDescription && <p>{errors.sourceDescription.message}</p>}
                                />
                            </div>
                            <p className='mt-1 text-sm text-gray-600'>Zadejte např. ISBN, odkaz na e-shop, kde knihu prodávají, nebo odkaz na článek pokud se jedná o online zdroj.</p>
                        </div>
                    )}

                    {sourceType === 'book_chapter' && (
                        <div className='space-y-3 rounded-md border border-gray-200 p-3'>
                            <div>
                                <Label htmlFor="bookSource" className="block mb-1">Kniha (ISBN / URL / název):<span className='text-red-500 ml-1'>*</span></Label>
                                <TextInput
                                    id="bookSource"
                                    placeholder="978-80-253-0501-0 nebo https://..."
                                    {...register('bookSource', { required: { value: true, message: 'Toto pole je povinné.' } })}
                                    color={errors.bookSource ? 'failure' : 'default'}
                                    helperText={errors.bookSource && <p>{errors.bookSource.message}</p>}
                                />
                            </div>
                            <div>
                                <Label htmlFor="chapterTitle" className="block mb-1">Název kapitoly:<span className='text-red-500 ml-1'>*</span></Label>
                                <TextInput
                                    id="chapterTitle"
                                    placeholder="Hyalohyphomycosis"
                                    {...register('chapterTitle', { required: { value: true, message: 'Toto pole je povinné.' } })}
                                    color={errors.chapterTitle ? 'failure' : 'default'}
                                    helperText={errors.chapterTitle && <p>{errors.chapterTitle.message}</p>}
                                />
                            </div>
                            <div>
                                <Label htmlFor="chapterAuthors" className="block mb-1">Autor(i) kapitoly <span className='text-gray-400 font-normal'>(pokud jiní než autoři knihy)</span>:</Label>
                                <TextInput
                                    id="chapterAuthors"
                                    placeholder="Nucci, M. a Anaissie, E. J."
                                    {...register('chapterAuthors')}
                                />
                            </div>
                            <div>
                                <Label htmlFor="chapterPages" className="block mb-1">Strany kapitoly:<span className='text-red-500 ml-1'>*</span></Label>
                                <TextInput
                                    id="chapterPages"
                                    placeholder="309–327"
                                    className='max-w-[12rem]'
                                    {...register('chapterPages', { required: { value: true, message: 'Toto pole je povinné.' } })}
                                    color={errors.chapterPages ? 'failure' : 'default'}
                                    helperText={errors.chapterPages && <p>{errors.chapterPages.message}</p>}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {!hasStarted && (
                    <div className='flex flex-row items-start gap-2 mt-4 mb-4 bg-gray-200 p-3 rounded-md'>
                        <AlertTriangleIcon size={20} className='flex-shrink-0 text-yellow-500' />
                        <div className='block text-sm text-gray-600'><strong>Upozornění:</strong> AI může vygenerovat nepřesné nebo zavádějící informace. Vždy si zkontrolujte, zda citace obsahuje správné informace a odpovídá citační normě Vašeho dokumentu. Pokud si nejste jisti, kontaktuje nás, pomůžeme Vám ověřit správnost údajů a formální náležitosti Vaší práce.</div>
                    </div>
                )}

                {hasStarted && (
                    <AITaskSteps
                        task={aiTask}
                        className='rounded-md h-[10rem] mt-4 mb-4'
                    />
                )}

                <div className='flex flex-col items-stretch gap-2'>
                    <div className='flex flex-row items-center justify-end gap-2'>
                        <Button
                            color="gray"
                            onClick={onClose}
                        >Zavřít</Button>

                        <div className='flex-grow' />

                        {(status === 'submitted' || status === 'streaming') && (
                            <Button
                                color='outline'
                                size='sm'
                                className='flex-shrink-0'
                                onClick={handleStop}
                            ><StopCircleIcon size={18} /> <span className='ml-2'>Zastavit</span></Button>
                        )}

                        {status === 'ready' && lastMessageText && (
                            <>
                                <Button
                                    color='primary'
                                    size='sm'
                                    className='flex-shrink-0'
                                    onClick={handleApply}
                                ><CheckIcon size={18} /> <span className='ml-2'>Použít</span></Button>
                                {hasNonLatinScript && (
                                    <Button
                                        color='blue'
                                        size='sm'
                                        className='flex-shrink-0'
                                        onClick={handleTranslate}
                                    ><Languages size={18} /> <span className='ml-2'>Přeložit citaci</span></Button>
                                )}
                                <Button
                                    color='outline'
                                    size='sm'
                                    className='flex-shrink-0'
                                    onClick={handleGenerate}
                                ><RefreshCwIcon size={18} /> <span className='ml-2'>Zkusit znovu</span></Button>
                            </>
                        )}

                        {status === 'ready' && !lastMessageText && (
                            <Button
                                color='primary'
                                className='flex-shrink-0'
                                onClick={handleGenerate}
                            ><SparklesIcon size={18} /> <span className='ml-2'>Vygenerovat citaci</span></Button>
                        )}

                        {status === 'error' && (
                            <Button
                                color='primary'
                                size='sm'
                                className='flex-shrink-0'
                                onClick={handleGenerate}
                            ><RefreshCwIcon size={18} /> <span className='ml-2'>Zkusit znovu</span></Button>
                        )}
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};
